import type { TimeEntryOverageStatus } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { AgreementOverageAlertCard } from './AgreementOverageAlertCard.js';
import { useAgreementHoursUsage } from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

interface AgreementHoursTabProps {
  agreementId: string;
  /** Whether the viewer may change the overage-alert recipients. */
  canEdit: boolean;
}

/** Badge for a managed-services hour's included / overage / split allocation. */
function AllocationBadge({ status }: { status: TimeEntryOverageStatus }): JSX.Element {
  if (status === 'overage') return <Badge tone="amber">Overage</Badge>;
  if (status === 'split') return <Badge tone="blue">Included + overage</Badge>;
  return <Badge tone="green">Included</Badge>;
}

interface SummaryCardProps {
  label: string;
  value: string;
  tone?: 'default' | 'amber';
}

function SummaryCard({ label, value, tone = 'default' }: SummaryCardProps): JSX.Element {
  return (
    <div
      className={`rounded-lg border p-4 ${
        tone === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'amber' ? 'text-amber-700' : 'text-slate-900'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

export function AgreementHoursTab({
  agreementId,
  canEdit,
}: AgreementHoursTabProps): JSX.Element {
  const usageQuery = useAgreementHoursUsage(agreementId);
  const usage = usageQuery.data;

  const monthLabel = usage
    ? new Date(`${usage.month}-01T00:00:00`).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <div className="space-y-6">
      <DataState
        isLoading={usageQuery.isLoading}
        isError={usageQuery.isError}
        error={usageQuery.error}
        onRetry={() => void usageQuery.refetch()}
        isEmpty={false}
      >
        {usage ? (
          <>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <SummaryCard label={`Hours used (${monthLabel})`} value={usage.hoursUsedThisMonth} />
              <SummaryCard
                label={usage.isProrated ? 'Hours included (prorated)' : 'Hours included'}
                value={usage.includedHours}
              />
              <SummaryCard
                label="Overage hours this month"
                value={usage.overageHours}
                tone={Number(usage.overageHours) > 0 ? 'amber' : 'default'}
              />
              <SummaryCard
                label="Overage total"
                value={currency.format(Number(usage.overageTotal))}
                tone={Number(usage.overageTotal) > 0 ? 'amber' : 'default'}
              />
            </dl>

            <div className="rounded-lg border border-slate-200">
              <div className="border-b border-slate-200 px-3 py-2">
                <span className="text-sm font-medium text-slate-700">
                  Hours this month ({monthLabel})
                </span>
              </div>
              {usage.entries.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">No hours logged this month.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Member</th>
                      <th className="px-3 py-2 font-medium">Notes</th>
                      <th className="px-3 py-2 font-medium">Allocation</th>
                      <th className="px-3 py-2 text-right font-medium">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {usage.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-3 py-2 text-slate-600">{formatDate(entry.workDate)}</td>
                        <td className="px-3 py-2 text-slate-600">{entry.memberName ?? '—'}</td>
                        <td className="px-3 py-2 text-slate-500">
                          {entry.ticketTitle ?? entry.notes ?? '—'}
                        </td>
                        <td className="px-3 py-2">
                          <AllocationBadge status={entry.status} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-800">
                          {entry.hours}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <div />
        )}
      </DataState>

      <AgreementOverageAlertCard agreementId={agreementId} canEdit={canEdit} />
    </div>
  );
}
